import {createApi} from '@reduxjs/toolkit/query/react';
import {envelopeDataTransformResponse, PrsStatus} from "core";
import queryString from 'query-string';

import {axiosBaseQuery} from '../axiosBaseQuery';

export type PrsSession = {
    name: string;
    id: string;
};

export type PrsCandidates = {
    name: string;
    id: string;
};

export type Prs = {
    id: string;
    referenceId: string;
    qualificationLevelName: string;
    serviceTypeId: string;
    serviceTypeName: string;
    createdOn: Date;
    status: PrsStatus;
    exam: string;
    prsComponents: PrsComponent[];
    deadlineHasPassed: boolean;
    taxRate: number;
};

export type PrsComponent = {
    component: string;
    fee: string;
    feeValue: number;
};

export type AvailableServiceType = {
  sessionId: number;
  sessionName: string;
  serviceTypeId: number;
  serviceTypeName: string;
  candidateId: number;
  candidateName: string;
  isAvailable: boolean;
};

export type AvailableExam = {
  registrationId: number;
  countryId: number;
  exam: string;
  prsStatus: string;
  deadline: string;
  taxRate: number;
};

export type CancellationReason = {
    id: number;
    shortCode: string;
    name: string;
}

export type AvailableComponent = {
    registrationId: number;
    componentId: number;
    component: string;
    fee: string;
    currencyCode: string;
    isDisabled: boolean;
}

export type GetPrsesToPayQueryResult = {
    prss: PrsToPay[];
    total: string;
    currency: string;
    deadline: string;
};

export type PrsToPay = {
  id: number;
  referenceId: string;
  examOptionCode: string;
  syllabusName: string;
  components: Component[];
  total: string;
  currency: string;
  taxRate: number;
};

export type  Component = {
    componentName: string;
    fee: number;
    currency: string;
};

export type PaymentDescription = {
    id: number;
    name: string;
    shortCode: string;
    description: string;
};

export type PrsPaymentSettings = {
    allowOfflinePayment: boolean;
    allowOnlinePayment: boolean;
    onlinePaymentsDescriptions: PaymentDescription[];
    offlinePaymentsDescriptions: PaymentDescription[];
};

export type StartOnlinePaymentForPrsRequest = {
  prsIds: number[];
  currencyCode: string;
  fee: number;
}

export type SessionsAvailableInPrs = {
  names: string[];
  isActivePrsBeforeDeadline: boolean;
}

export type PayMethodType = 'Online' | 'Offline';

export const postResultsServicesApi = createApi({
    reducerPath: 'postResultsServices',
    tagTypes: ['Prses', 'PrsCancellationReasons', 'PrsSessions'],
    baseQuery: axiosBaseQuery({
        baseUrl: '/web-api/b2c/api/prs',
        paramsSerializer: (params: any) => queryString.stringify(params, {arrayFormat: 'none'}),
    }),
    endpoints: (builder) => ({
        getSessions: builder.query<PrsSession[], void>({
            query: () => ({
                url: '/sessions',
                method: 'GET',
            }),
            transformResponse: envelopeDataTransformResponse,
            providesTags: ['PrsSessions'],
        }),
        getAvailableServiceTypes: builder.query<AvailableServiceType[], void>({
            query: () => ({
                url: '/availableServiceTypes',
                method: 'GET',
            }),
            transformResponse: envelopeDataTransformResponse,
        }),
        getSessionsAvailableInPrs: builder.query<SessionsAvailableInPrs, void>({
            query: () => ({
                url: '/sessionsAvailableInPrs',
                method: 'GET',
            }),
            transformResponse: envelopeDataTransformResponse
        }),
        getExams: builder.mutation<AvailableExam[], { sessionId: number, candidateId: number, serviceTypeId: number }>({
            query: (request) => ({
                url: '/exams',
                method: 'GET',
                params: request
            }),
            transformResponse: envelopeDataTransformResponse,
        }),
        getComponents: builder.mutation<AvailableComponent[], { registrationId: number, serviceTypeId: number }>({
            query: (request) => ({
                url: '/components',
                method: 'GET',
                params: request
            }),
            transformResponse: envelopeDataTransformResponse,
        }),
        getCandidates: builder.query<PrsCandidates[], (number | string)>({
            query: (sessionId) => ({
                url: '/candidates',
                method: 'GET',
                params: {sessionId}
            }),
            transformResponse: envelopeDataTransformResponse,
            providesTags: ['Prses'],
        }),
        getCancellationReasons: builder.query<CancellationReason[], void>({
            query: () => ({
                url: '/cancellationReasons',
                method: 'GET',
            }),
            transformResponse: envelopeDataTransformResponse,
            providesTags: ['PrsCancellationReasons'],
        }),
        getPrss: builder.query<Prs[], { sessionId: string, candidateId: string }>({
            query: (request) => ({
                url: '/list',
                method: 'GET',
                params: request
            }),
            transformResponse: envelopeDataTransformResponse,
            providesTags: ['Prses'],
        }),
        getPrsToPay: builder.query<GetPrsesToPayQueryResult, { prsIds: string[] }>({
            query: (request) => ({
                url: '/prsToPay',
                method: 'GET',
                params: request,
            }),
            transformResponse: envelopeDataTransformResponse,
        }),
        getPrsPaymentSettings: builder.query<PrsPaymentSettings, { prsIds: string[] }>({
            query: (request) => ({
                url: `/prsPaymentSettings`,
                method: 'get',
                params: request,
            }),
            transformResponse: envelopeDataTransformResponse,
        }),
        createPrs: builder.mutation<number, { registrationId: number, candidateId: number, components: number[], serviceTypeId: number }>({
            query: (request) => ({
                url: '',
                method: 'POST',
                data: request,
            }),
            transformResponse: envelopeDataTransformResponse,
            invalidatesTags: ['Prses', 'PrsSessions'],
        }),
        cancelPrses: builder.mutation<void, { candidateId: string, prsesIds: string[], cancellationReasonId: string, cancellationInfo: string }>({
            query: (request) => ({
                url: '/cancelPrses',
                method: 'POST',
                data: request,
            }),
            transformResponse: envelopeDataTransformResponse,
            invalidatesTags: ['Prses'],
        }),
        pay: builder.mutation<{ paymentUrl: string }, StartOnlinePaymentForPrsRequest>({
            query: (request) => ({
                url: '/pay',
                method: 'POST',
                data: request,
            }),
            transformResponse: envelopeDataTransformResponse,
            invalidatesTags: ['Prses'],
        }),
    }),
});

export const {
    useGetCancellationReasonsQuery,
    useLazyGetSessionsQuery,
    useLazyGetCandidatesQuery,
    useGetExamsMutation,
    useGetPrsToPayQuery,
    useGetPrsPaymentSettingsQuery,
    useGetComponentsMutation,
    useCancelPrsesMutation,
    usePayMutation,
    useLazyGetPrssQuery,
    useLazyGetAvailableServiceTypesQuery,
    useCreatePrsMutation,
    useLazyGetSessionsAvailableInPrsQuery
} = postResultsServicesApi;
